import { getSketchObjectWidthProperty } from '@pointorama/potree-shared';
import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushArc, pushRect, pushSegment } from '../glyphs/frame';
import { beginGlyphInstance, endGlyphInstance } from '../glyphs/glyphInstances';
import { getNumberParamProperty, type ParamValues } from '../paramSchema';
import { t } from '../../translation/markers';

const PARAMS = {
  moduleWidth: { kind: 'float', label: t('module width'), default: 0.6, min: 0.2, max: 1.2, decimals: 2, distance: true },
  moduleCount: { kind: 'int', label: t('number of cabinets'), default: 2, min: 1 },
  doorsOpen: { kind: 'boolean', label: t('show door swings'), default: false },
  worktop: { kind: 'boolean', label: t('worktop'), default: true },
} as const;

/** Two coupled module values never fight below this difference (m). */
const MODULE_EPS = 1e-4;

/**
 * Keep `module width` and `number of cabinets` consistent with each other and
 * with the run length — the cabinet twin of the stair's step ↔ step count
 * sync (see stair.ts for the convergence contract).
 */
function syncCabinetParams(objectId: string, changedKey: string): void {
  if (changedKey !== 'width' && changedKey !== 'moduleWidth' && changedKey !== 'moduleCount') return;
  let runLength: number;
  try {
    runLength = getSketchObjectWidthProperty(objectId).value ?? 0;
  } catch {
    return; // property service not ready
  }
  if (runLength <= 0) return;
  const moduleWidthProperty = getNumberParamProperty('base_cabinet', 'moduleWidth', objectId);
  const countProperty = getNumberParamProperty('base_cabinet', 'moduleCount', objectId);
  if (changedKey === 'moduleCount') {
    const count = Math.max(1, Math.round(countProperty.value ?? 1));
    const moduleWidth = runLength / count;
    if (Math.abs((moduleWidthProperty.value ?? 0) - moduleWidth) > MODULE_EPS) moduleWidthProperty.value = moduleWidth;
  } else {
    const moduleWidth = Math.max(0.05, moduleWidthProperty.value ?? 0.6);
    const count = Math.max(1, Math.round(runLength / moduleWidth));
    if (countProperty.value !== count) countProperty.value = count;
  }
}

/** Worktop overhang past the carcass on the front and both ends (m). */
const WORKTOP_OVERHANG = 0.02;

/**
 * Kitchen base-cabinet RUN, Revit-style: the user draws a span along a wall
 * (LINE anchor + WALL constraint) and the glyph parametrically SKINS that
 * length — the run is divided into as many `moduleWidth`-sized cabinet
 * modules as fit, each drawn with its divider, open leaf and swing arc
 * (alternating hinge sides). Resizing the run or changing the module width
 * reflows the modules. The back (local -depth/2) rests against the wall.
 */
function buildCabinetGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  pushRect(out, frame, -hw, hw, -hd, hd);

  if (params.worktop) {
    pushRect(out, frame, -hw - WORKTOP_OVERHANG, hw + WORKTOP_OVERHANG, -hd, hd + WORKTOP_OVERHANG);
  }

  // Skin the run: as many modules of ~moduleWidth as fit (at least one),
  // stretched evenly so they exactly fill the drawn span.
  const moduleWidth = Math.max(0.05, params.moduleWidth);
  const moduleCount = Math.max(1, Math.round(frame.width / moduleWidth));
  const actualModuleWidth = frame.width / moduleCount;

  // Dividers between modules, across the carcass depth.
  for (let i = 1; i < moduleCount; i++) {
    const u = -hw + i * actualModuleWidth;
    pushSegment(out, at(frame, u, -hd), at(frame, u, hd), frame.z);
  }

  if (params.doorsOpen) {
    // Per module: an open leaf standing out from the front face at the hinge,
    // plus the quarter-circle swing arc back to the latch. Hinges alternate
    // ends so adjacent doors swing toward each other.
    for (let i = 0; i < moduleCount; i++) {
      const u0 = -hw + i * actualModuleWidth;
      const u1 = u0 + actualModuleWidth;
      const hingeAtStart = i % 2 === 0;
      const hingeU = hingeAtStart ? u0 : u1;
      // Alternating hinge sides are true mirrors — two instance keys, so the
      // DXF export gets one block per swing direction.
      beginGlyphInstance(out, `door_${hingeAtStart ? 's' : 'e'}_${Math.round(actualModuleWidth * 1000)}`, {
        center: at(frame, hingeU, hd),
        dir: frame.dir,
        norm: frame.norm,
        width: actualModuleWidth,
        depth: actualModuleWidth,
        z: frame.z,
      });
      pushSegment(out, at(frame, hingeU, hd), at(frame, hingeU, hd + actualModuleWidth), frame.z);
      // Arc from the open leaf tip (90°, +norm) down to the latch on the front
      // face (0° toward +u for a start hinge, 180° toward -u for an end hinge).
      pushArc(out, frame, hingeU, hd, actualModuleWidth, Math.PI / 2, hingeAtStart ? 0 : Math.PI);
      endGlyphInstance(out);
    }
  }

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'base_cabinet',
    label: t('Base cabinets'),
    category: SketchObjectCategory.KITCHEN,
    anchor: SketchObjectAnchorKind.LINE,
    constraint: { kind: 'WALL', defaultInset: 0 },
    defaultSize: { width: 1.2, depth: 0.6 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildCabinetGlyph },
    syncParams: syncCabinetParams,
  }),
);
